import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DropdownItems } from '@material-library';
import { ApiService } from 'src/app/core/api.service';
import { Base64Service } from 'src/app/core/base64.service';
import { UserService } from 'src/app/core/user.service';
import { BlendEvent, ErrCode, LinkListItem } from 'src/app/shared/models';
import { IFile } from 'src/sync';
import { LinkListService } from 'src/app/links/services/link-list.service';
import { renameLinkOptions } from 'src/app/util/blend-property-names';
import { BlendService } from 'src/app/shared/services/blend.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { Store } from '@ngrx/store';
import * as fromRoot from 'src/app/reducers';
import * as LinkListActions from 'src/app/actions/link-list.actions';
import { Clipboard } from '@angular/cdk/clipboard';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { LinkFormFields, mapFormToApiParams } from '../link-permissions-form/link-permissions-form.component';

interface Fields {
  label: FormControl<string>;
  key: FormControl<string>;
  download_limit: FormControl<number>;
  email_notification: FormControl<string>;
  compat: FormControl<string>;
  allow_comment: FormControl<string>;
  comment_notification: FormControl<string>;
  permissionsForm: FormControl<LinkFormFields>;
}

import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DirtyOutService } from 'src/app/core/crypt/dirty-out.service';
import { NotificationsService } from 'src/app/core/notifications.service';
import { FeatureService } from 'src/app/shared/services/feature.service';
import { LoggerService } from 'src/app/core/logger.service';
import { ChargebeeService } from 'src/app/shared/services/chargebee.service';

@Component({
  selector: 'app-link-manage',
  templateUrl: './link-manage.component.html',
  styleUrls: ['./link-manage.component.scss']
})
export class LinkManageComponent implements OnInit, OnDestroy {
  @Input() link?: any;
  @Input() fileType: string;
  @Input() fileName: string;
  @Input() showManageLinkDialog: boolean;
  @Input() item: LinkListItem | IFile;
  
  @Output() state = new EventEmitter<number>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close = new EventEmitter<boolean>();
  @Output() showManageLinkDialogChange = new EventEmitter<boolean>();
  @Output() formDataSaved = new EventEmitter<any>();
  
  public isPro = 0;
  public linkSendResult = 0;
  public isEmailLinksAllowed = true;
  public isEnced = true;
  
  public errcode: ErrCode;
  public decodedLabel: string;
  
  public isButtonLoading: boolean = false;
  public specificPermissionList: DropdownItems[] = [];
  public shareLinkUrl: string;
  public link_expired: boolean = false;
  public showSettingsDialog: boolean = false;
  public linkForm: FormGroup<Fields>;
  
  public hasComment = false;
  public hasBetaAccess = false;
  public isAppLink = false;
  public isSmallScreen: boolean;
  private breakpointSubscription: Subscription;
  public total = 0;
  public finished = 0;
  public modalText: string;
  private isComponentActive = true;
  
  constructor(
    private api: ApiService,
    private base64: Base64Service,
    private fb: FormBuilder,
    private userService: UserService,
    private linkList: LinkListService,
    private blendService: BlendService,
    private snackbarService: SnackbarService,
    private store: Store<fromRoot.State>,
    private clipboard: Clipboard,
    public breakpointObserver: BreakpointObserver,
    private router: Router,
    private loggerService: LoggerService,
    private dirtyOut: DirtyOutService,
    private notificationsService: NotificationsService,
    public featureService: FeatureService,
    private linkListService: LinkListService,
    private chargebeeService: ChargebeeService
  ) { }

  async ngOnInit() {
    this.breakpointSubscription = this.breakpointObserver.observe([
      '(max-width: 599px)',
    ])
      .subscribe((state: BreakpointState) => {
        this.isSmallScreen = state.breakpoints['(max-width: 599px)'];
      });
    
    this.decodedLabel = this.base64.decode(this.link.label);
    
    this.linkForm = this.fb.group({
      label: [this.decodedLabel],
      key: [this.link.key],
      download_limit: [this.link.download_limit],
      email_notification: [
        this.link.email_notification.toString() || '0',
        Validators.required,
      ],
      compat: [this.link.compat.toString() || '0', Validators.required],
      allow_comment: [
        this.link.allow_comment.toString() || '1',
        Validators.required,
      ],
      comment_notification: [
        this.link.comment_notification.toString() || '1',
        Validators.required,
      ],
      permissionsForm: [],
    });
    
    this.shareLinkUrl = this.link.linkUrl;
    
    this.isPro = this.userService.get('is_pro');
    this.isEmailLinksAllowed = this.userService.isEmailLinksAllowed();
    this.hasComment = this.userService.get('has_comment');
    this.hasBetaAccess = this.userService.get('has_beta_access');
    this.isAppLink = this.link.baseUrl.includes('/al');
    this.isEnced = this.link.cnt < 0;
    
    this.showManageLinkDialog = true;
    this.setState(1);
    
    if (!this.isEnced) {
      this.encLink();
    }
  }
  
  public ngOnDestroy(): void {
    this.breakpointSubscription?.unsubscribe();
    this.notificationsService.startNotificationLoop();
    this.isComponentActive = false;
  }
  
  public setState(state: number) {
    this.state.emit(state);
  }
  
  public async encLink() {
    this.notificationsService.stopNotificationLoop();
    
    await this.linkListService.encryptLink(
      this.link,
      (progress: number) => this.modalText = `Encrypting link ${progress}%, Please wait...`,
      () => this.isComponentActive
    );
    this.modalText = 'Finalizing Link, Please wait...';
    this.isEnced = true;
    this.notificationsService.startNotificationLoop();
  }
  
  public handleCopy(url: string) {
    this.clipboard.copy(url);
    this.snackbarService.showMessage("success", "Link copied.");
    this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'copy link' });
  }
  
  public handleOpen() {
    this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'open link' });
  }
  
  public onSubmit = async () => {
    if (!this.linkForm.valid) {
      return;
    }
    this.isButtonLoading = true;
    
    this.errcode = null;
    let expServtime;
    const { value: formValue } = this.linkForm.controls.permissionsForm;
    if (formValue.use_link_expiry) {
      const expDate = formValue.exp_date;
      const [hours, minutes] = formValue.exp_time.split(':');
      const dateTime = new Date(
        expDate.getFullYear(),
        expDate.getMonth(),
        expDate.getDate(),
        parseInt(hours, 10),
        parseInt(minutes, 10),
        0
      );
      
      expServtime = dateTime.getTime();
    }
    
    const downloadLimit = this.linkForm.get('download_limit').value || 0;
    
    const allowComment = parseInt(this.linkForm.get('allow_comment').value, 10);
    
    const linkSetOptions = {
      sync_id: this.link.sync_id,
      share_id: this.link.share_id,
      publink_id: this.link.cachekey,
      label: this.base64.encode(this.linkForm.get('label').value),
      exp_servtime: expServtime,
      download_limit: downloadLimit,
      email_notification: parseInt(this.linkForm.get('email_notification').value, 10),
      compat: parseInt(this.linkForm.get('compat').value, 10),
      allow_comment: allowComment,
      comment_notification: parseInt(this.linkForm.get('comment_notification').value, 10),
      ...mapFormToApiParams(formValue.permission, formValue.upload, this.link.type)
    };
    
    const password = formValue.use_password ? this.base64.encode(formValue.passwordprotect) : null;
    
    try {
      await this.api.execute('linksetoptions', linkSetOptions);
      
      await this.api.execute('linksetpassword', {
        sync_id: this.link.sync_id,
        share_id: this.link.share_id,
        publink_id: this.link.cachekey,
        password: password,
      });
      
      const formData = this.linkForm.controls.permissionsForm.value;
      this.formDataSaved.emit(formData);
      
      this.blendService.track(BlendEvent.SAVE_LINK_SETTINGS, renameLinkOptions({
        ...linkSetOptions,
        type: this.link.type,
        file_extension: this.item.file_extension
      }));
      
      this.store.dispatch(new LinkListActions.LinkListRefreshAction());
      this.snackbarService.showMessage("success", "Link settings successfully saved.");
    } catch (error) {
      this.errcode = ErrCode.fromException(error);
    } finally {
      this.isButtonLoading = false;
    }
  };
  
  public onBack = () => {
    this.closeManageLinkDialog();
  }
  
  public closeManageLinkDialog = () => {
    if (this.showManageLinkDialog && !this.isEnced) {
      if (!window.confirm('Are you sure you want to close this? This will continue to process in the background')) {
        return;
      }
      this.snackbarService.showMessage('default', 'The action will continue processing until finished.');
    }
    this.showManageLinkDialog = false;
    setTimeout(() => {
      this.showManageLinkDialogChange.next(false);
      this.close.emit(false);
    });
  }
  
  public onAdvanceSettings = () => {
    const linkManageFormData = this.linkForm.controls.permissionsForm.value;
    this.formDataSaved.emit(linkManageFormData);
    this.closeManageLinkDialog();
    setTimeout(() => {
      this.setState(2);
    })
    this.close.emit(false);
  }
  
  public onShareViaEmail = () => {
    this.closeManageLinkDialog();
    setTimeout(() => {
      this.setState(4);
    })
    this.close.emit(false);
  }

  onUpgradeClick = ()=>{
    this.chargebeeService.openPortal();
  }
  
  get isActionButtonDisabled(): boolean {
    return this.linkForm.invalid;
  }
}
